/* eslint-disable no-undef */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PORTAL_CONTEXT_KEY, PORTAL_SELECTED_PROFILE_KEY, loadGuestUser, getUserInfo } from 'modules/user/userSlice';
import { PublicClientApplication, AuthError } from '@azure/msal-browser';
import { getI18n } from 'react-i18next';
import qs from 'qs';
import { AppThunk } from 'store';
import { getAzureClient, setAzureClient, setCurrentAccount, setGuestAccessToken } from 'utils/authClient';
import { parseEnteprisesWithSignPolicy } from 'utils/sign-policy';
import createUserApi, { getGuestAccessToken } from 'api/user';
import { showToast } from 'modules/app/toasterSlice';
import { getPartnerSettings, setPartnerSettings } from 'utils/partnerSettings';
import { setEnvironment, setPartnerSite, setAccountType } from 'utils/tags';

const FORGOT_PASSWORD_ERROR_CODE = 'AADB2C90118';
const USER_ACTION_CANCEL_ERROR_CODE = 'AADB2C90091';
const USER_NOT_EXIST_ERROR_CODE = 'AADB2C99002';

export interface AuthQuery {
  failedRedirectUrl?: string;
}

export interface GuestAuthQuery {
  inviteId: string;
  claimCode: string;
}

interface State {
  loading: boolean;
  isAuthenticated: boolean;
  ready: boolean;
}

const initialState: State = {
  loading: true,
  isAuthenticated: false,
  ready: false,
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.loading = action.payload;
      return state;
    },

    setIsAuthenticated: (state: State, action: PayloadAction<boolean>): State => {
      state.isAuthenticated = action.payload;
      return state;
    },

    setReady: (state: State, action: PayloadAction<boolean>): State => {
      state.ready = action.payload;
      return state;
    },
  },
});

const { setLoading, setIsAuthenticated, setReady } = slice.actions;
export { setIsAuthenticated };
export default slice.reducer;

function getDomainName(): string {
  const domain = window.location.pathname.replace(/^\/|\/$/g, '');
  return domain;
}

export const bootstrapAuth = (redirectUri: string, query: AuthQuery): AppThunk => async (dispatch): Promise<void> => {
  const CLIENT_ID = process.env.REACT_APP_AZURE_APPLICATION_ID ?? '';
  const LOGIN_INSTANCE = process.env.REACT_APP_AZURE_LOGIN_INSTANCE ?? '';
  const TENANT = process.env.REACT_APP_AZURE_TENAT ?? '';
  const DEFAULT_SIGN_POLICY = process.env.REACT_APP_AZURE_SIGN_POLICY ?? '';
  const ENTERPRISE_POLICIES = process.env.REACT_APP_ENTERPRISE_OPTIONS ?? '';
  const SCOPES = (process.env.REACT_APP_AZURE_SCOPES ?? '').split(' ');
  const FORGOT_PASSWORD_POLICY = process.env.REACT_APP_FORGOT_PASSWORD_POLICY ?? '';

  const availablePolicies = parseEnteprisesWithSignPolicy(ENTERPRISE_POLICIES);
  const currentDomain = getDomainName();
  const signPolicy = availablePolicies.get(currentDomain) ?? DEFAULT_SIGN_POLICY;

  const msalApp = new PublicClientApplication({
    auth: {
      clientId: CLIENT_ID,
      authority: `${LOGIN_INSTANCE}${TENANT}${signPolicy}`,
      knownAuthorities: [`${LOGIN_INSTANCE}${TENANT}${signPolicy}`],
      navigateToLoginRequestUrl: true,
      redirectUri,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  });
  setAzureClient(msalApp);
  try {
    await msalApp.handleRedirectPromise();

    const settings = await createUserApi('wechooose').getSettingsForUser();
    setPartnerSettings({
      logoUrl: settings?.logoUrl ?? '',
      colors: settings?.colors,
      faviconUrl: settings?.faviconUrl ?? '',
      partnershipId: settings?.partnershipId ?? '',
      currencies: settings?.currencies || [],
      disableSignupLink: (settings?.features || []).includes('DisableSignUp'),
      customerFees: settings.customerFees,
      termsAndConditionsSupplyParagraphs: settings.termsAndConditionsSupplyParagraphs,
      refundPolicy: settings.refundPolicy,
      feeDisplay: settings.feeDisplay,
    });

    const accounts = msalApp.getAllAccounts();
    if (accounts?.length > 0) {
      const account = accounts.find(a => a.homeAccountId.includes(signPolicy.toLowerCase())) || accounts[0];
      setCurrentAccount(account);
      dispatch(setIsAuthenticated(true));
    } else {
      dispatch(setIsAuthenticated(false));
    }
  } catch (err) {
    const settings = getPartnerSettings();
    if (((err as AuthError)?.message ?? '').includes(USER_ACTION_CANCEL_ERROR_CODE)) {
      window.location.replace(window.origin);
      return;
    }
    if (((err as AuthError)?.message ?? '').includes(USER_NOT_EXIST_ERROR_CODE)) {
      await msalApp.loginRedirect({
        authority: `${LOGIN_INSTANCE}${TENANT}${signPolicy}`,
        scopes: SCOPES,
        extraQueryParameters: {
          logoUrl: encodeURIComponent(settings?.logoUrl),
          weChoooseSignupLink: encodeURIComponent(`${window.origin}/signup/wechooose`),
          colors: encodeURIComponent(JSON.stringify(settings?.colors)),
          faviconUrl: encodeURIComponent(settings?.faviconUrl),
          disableSignupLink: encodeURIComponent(`${settings?.disableSignupLink ?? false}`),
          lng: encodeURIComponent(`${getI18n().language || 'en'}`),
          errorCode: encodeURIComponent(`${USER_NOT_EXIST_ERROR_CODE || ''}`),
        },
      });
    }
    if (((err as AuthError)?.message ?? '').includes(FORGOT_PASSWORD_ERROR_CODE)) {
      await msalApp.loginRedirect({
        authority: `${LOGIN_INSTANCE}${TENANT}${FORGOT_PASSWORD_POLICY}`,
        scopes: SCOPES,
        extraQueryParameters: {
          logoUrl: encodeURIComponent(settings?.logoUrl),
          weChoooseSignupLink: encodeURIComponent(`${window.origin}/signup/wechooose`),
          colors: encodeURIComponent(JSON.stringify(settings?.colors)),
          faviconUrl: encodeURIComponent(settings?.faviconUrl),
          disableSignupLink: encodeURIComponent(`${settings?.disableSignupLink ?? false}`),
          lng: encodeURIComponent(`${getI18n().language || 'en'}`),
        },
      });
    } else if (query?.failedRedirectUrl) {
      await msalApp.logoutRedirect({
        postLogoutRedirectUri: query.failedRedirectUrl,
      });
    } else {
      await msalApp.logoutRedirect();
    }
  }
  dispatch(setReady(true));
};

export const authorizeUser = (): AppThunk => async (dispatch): Promise<void> => {
  dispatch(getUserInfo());
  dispatch(setLoading(false));
};

export const bootstrapGuestAuth = (query: GuestAuthQuery): AppThunk => async (dispatch): Promise<void> => {
  try {
    dispatch(setLoading(true));
    if (!query || !query.inviteId || !query.claimCode) {
      dispatch(setLoading(false));
      return;
    } else {
      const token = await getGuestAccessToken(query.inviteId, query.claimCode);
      setGuestAccessToken(token);
      dispatch(setIsAuthenticated(true));
      dispatch(loadGuestUser(query.inviteId));
      dispatch(setLoading(false));
    }
  } catch (err) {
    dispatch(setLoading(false));
  }
};

export const logout = (redirectUrl?: string): AppThunk => (): void => {
  localStorage.removeItem(PORTAL_CONTEXT_KEY);
  localStorage.removeItem(PORTAL_SELECTED_PROFILE_KEY);
  const azureClient = getAzureClient();
  azureClient.logoutRedirect({
    postLogoutRedirectUri: redirectUrl,
  });
};

export const resetPassword = (): AppThunk => (): void => {
  const LOGIN_INSTANCE = process.env.REACT_APP_AZURE_LOGIN_INSTANCE ?? '';
  const TENANT = process.env.REACT_APP_AZURE_TENAT ?? '';
  const FORGOT_PASSWORD_POLICY = process.env.REACT_APP_FORGOT_PASSWORD_POLICY ?? '';
  const SCOPES = (process.env.REACT_APP_AZURE_SCOPES ?? '').split(' ');
  const azureClient = getAzureClient();
  const settings = getPartnerSettings();
  try {
    azureClient.loginRedirect({
      authority: `${LOGIN_INSTANCE}${TENANT}${FORGOT_PASSWORD_POLICY}`,
      scopes: SCOPES,
      extraQueryParameters: {
        logoUrl: encodeURIComponent(settings?.logoUrl),
        weChoooseSignupLink: encodeURIComponent(`${window.origin}/signup/wechooose`),
        colors: encodeURIComponent(JSON.stringify(settings?.colors)),
        faviconUrl: encodeURIComponent(settings?.faviconUrl),
        disableSignupLink: encodeURIComponent(`${settings?.disableSignupLink ?? false}`),
        lng: encodeURIComponent(`${getI18n().language || 'en'}`),
      },
    });
  } catch (err) {
    //err
  }
};

export const signUpToWeChooose = (query: string): AppThunk => (dispatch): void => {
  const LOGIN_INSTANCE = process.env.REACT_APP_AZURE_LOGIN_INSTANCE ?? '';
  const TENANT = process.env.REACT_APP_AZURE_TENAT ?? '';
  const SIGN_UP_POLICY = process.env.REACT_APP_SIGN_UP_POLICY ?? '';
  const SCOPES = (process.env.REACT_APP_AZURE_SCOPES ?? '').split(' ');
  const azureClient = getAzureClient();
  const settings = getPartnerSettings();
  const params: { name?: string } = qs.parse(query, { ignoreQueryPrefix: true });
  const partnerSite = setPartnerSite();

  try {
    azureClient.loginRedirect({
      authority: `${LOGIN_INSTANCE}${TENANT}${SIGN_UP_POLICY}`,
      scopes: SCOPES,
      redirectStartPage: `${window.origin}/setup/wechooose${query}`,
      extraQueryParameters: {
        logoUrl: encodeURIComponent(settings?.logoUrl),
        weChoooseSignupLink: encodeURIComponent(`${window.origin}/signup/wechooose`),
        colors: encodeURIComponent(JSON.stringify(settings?.colors)),
        faviconUrl: encodeURIComponent(settings?.faviconUrl),
        disableSignupLink: encodeURIComponent(`${settings?.disableSignupLink ?? false}`),
        lng: encodeURIComponent(`${getI18n().language || 'en'}`),
        customerCompany: params.name ?? '',
        partnerSite: partnerSite,
        accountType: setAccountType(partnerSite),
        partnershipId: settings?.partnershipId ?? '',
        environment: setEnvironment(),
      },
    });
    sessionStorage.setItem('companyName', params.name ?? '');
  } catch (err) {
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};

export const signUpToMyChooose = (query: string): AppThunk => (dispatch): void => {
  const LOGIN_INSTANCE = process.env.REACT_APP_AZURE_LOGIN_INSTANCE ?? '';
  const TENANT = process.env.REACT_APP_AZURE_TENAT ?? '';
  const SIGN_UP_POLICY = process.env.REACT_APP_MYCHOOOSE_SIGN_UP_POLICY ?? '';
  const SCOPES = (process.env.REACT_APP_AZURE_SCOPES ?? '').split(' ');
  const azureClient = getAzureClient();
  const settings = getPartnerSettings();

  try {
    azureClient.loginRedirect({
      authority: `${LOGIN_INSTANCE}${TENANT}${SIGN_UP_POLICY}`,
      scopes: SCOPES,
      redirectStartPage: `${window.origin}/setup/mychooose${query}`,
      extraQueryParameters: {
        logoUrl: encodeURIComponent(settings?.logoUrl),
        weChoooseSignupLink: encodeURIComponent(`${window.origin}/signup/wechooose`),
        colors: encodeURIComponent(JSON.stringify(settings?.colors)),
        faviconUrl: encodeURIComponent(settings?.faviconUrl),
        lng: encodeURIComponent(`${getI18n().language || 'en'}`),
        customerCompany: 'private',
        partnerSite: 'mychooose',
        accountType: 'private',
        partnershipId: settings?.partnershipId ?? '',
        environment: setEnvironment(),
      },
    });
  } catch (err) {
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};

export const signInInvite = (query: string, customerCompany: string): AppThunk => (): void => {
  const LOGIN_INSTANCE = process.env.REACT_APP_AZURE_LOGIN_INSTANCE ?? '';
  const TENANT = process.env.REACT_APP_AZURE_TENAT ?? '';
  const SIGNUP_SIGN_IN_POLICY = process.env.REACT_APP_INVITE_SIGN_UP_SIGN_IN ?? '';
  const SCOPES = (process.env.REACT_APP_AZURE_SCOPES ?? '').split(' ');
  const azureClient = getAzureClient();
  const settings = getPartnerSettings();
  const partnerSite = setPartnerSite();

  try {
    azureClient.loginRedirect({
      authority: `${LOGIN_INSTANCE}${TENANT}${SIGNUP_SIGN_IN_POLICY}`,
      scopes: SCOPES,
      redirectStartPage: `${window.origin}/invite${query}`,
      extraQueryParameters: {
        logoUrl: encodeURIComponent(settings?.logoUrl),
        weChoooseSignupLink: encodeURIComponent(`${window.origin}/signup/wechooose`),
        colors: encodeURIComponent(JSON.stringify(settings?.colors)),
        faviconUrl: encodeURIComponent(settings?.faviconUrl),
        disableSignupLink: encodeURIComponent(`${settings?.disableSignupLink ?? false}`),
        lng: encodeURIComponent(`${getI18n().language || 'en'}`),
        customerCompany: customerCompany,
        partnerSite: partnerSite,
        accountType: setAccountType(partnerSite),
        partnershipId: settings?.partnershipId ?? '',
        environment: setEnvironment(),
      },
    });
  } catch (err) {
    //err
  }
};

export const signInOrSignUpGuest = (query: string, context: 'wechooose' | 'mychooose'): AppThunk => (): void => {
  const LOGIN_INSTANCE = process.env.REACT_APP_AZURE_LOGIN_INSTANCE ?? '';
  const TENANT = process.env.REACT_APP_AZURE_TENAT ?? '';
  const SIGNUP_SIGN_IN_POLICY =
    context === 'wechooose' ? process.env.REACT_APP_GUEST_SIGNIN_SIGNUP_WECHOOOSE ?? '' : process.env.REACT_APP_GUEST_SIGNIN_SIGNUP_MYCHOOOSE ?? '';
  const SCOPES = (process.env.REACT_APP_AZURE_SCOPES ?? '').split(' ');
  const azureClient = getAzureClient();
  const settings = getPartnerSettings();

  try {
    azureClient.loginRedirect({
      authority: `${LOGIN_INSTANCE}${TENANT}${SIGNUP_SIGN_IN_POLICY}`,
      scopes: SCOPES,
      redirectStartPage: `${window.origin}/login-setup${query}`,
      extraQueryParameters: {
        logoUrl: encodeURIComponent(settings?.logoUrl),
        weChoooseSignupLink: encodeURIComponent(`${window.origin}/signup/wechooose`),
        colors: encodeURIComponent(JSON.stringify(settings?.colors)),
        faviconUrl: encodeURIComponent(settings?.faviconUrl),
        disableSignupLink: encodeURIComponent(`${settings?.disableSignupLink ?? false}`),
        lng: encodeURIComponent(`${getI18n().language || 'en'}`),
      },
    });
  } catch (err) {
    //err
  }
};

export const signInOrSignUpFacilitatedOrderClaim = (query: string): AppThunk => (): void => {
  const LOGIN_INSTANCE = process.env.REACT_APP_AZURE_LOGIN_INSTANCE ?? '';
  const TENANT = process.env.REACT_APP_AZURE_TENAT ?? '';
  const SIGNUP_SIGN_IN_POLICY = process.env.REACT_APP_AZURE_SIGN_POLICY ?? '';
  const SCOPES = (process.env.REACT_APP_AZURE_SCOPES ?? '').split(' ');
  const azureClient = getAzureClient();
  const settings = getPartnerSettings();

  try {
    azureClient.loginRedirect({
      authority: `${LOGIN_INSTANCE}${TENANT}${SIGNUP_SIGN_IN_POLICY}`,
      scopes: SCOPES,
      redirectStartPage: `${window.origin}/offset-confirmation${query}`,
      extraQueryParameters: {
        logoUrl: encodeURIComponent(settings?.logoUrl),
        weChoooseSignupLink: encodeURIComponent(`${window.origin}/signup/wechooose${query}`),
        colors: encodeURIComponent(JSON.stringify(settings?.colors)),
        disableSignupLink: encodeURIComponent(`${settings?.disableSignupLink ?? false}`),
        lng: encodeURIComponent(`${getI18n().language || 'en'}`),
      },
    });
  } catch (err) {
    //err
  }
};
